/**
 * GCSA 官网页脚 — 固化文案与绝对链接（完全复刻用）
 * 主站：https://gcsa.org · 媒体：https://media.gcsa.org
 *
 * 导航顺序与主站 AppFooter 一致（见 sync-footer-from-gcsa.mjs）
 */

import type { SiteFooterLocale } from './siteLocales';
import { GCSA_FOOTER_FROM_SITE_BY_LOCALE } from './gcsaFooterContentFromSite';

export type { SiteFooterLocale };

export {
  GCSA_CONTACT_EMAIL,
  GCSA_MAIN_ORIGIN,
  GCSA_MEDIA_ORIGIN,
  GCSA_TELEGRAM_URL,
  GCSA_X_URL
} from './gcsaSiteOrigins';

import {
  GCSA_CONTACT_EMAIL,
  GCSA_MAIN_ORIGIN,
  GCSA_MEDIA_ORIGIN,
  GCSA_TELEGRAM_URL,
  GCSA_X_URL
} from './gcsaSiteOrigins';

export type SiteFooterNavIcon =
  | 'home'
  | 'products'
  | 'media'
  | 'forums'
  | 'aiResearchInstitute'
  | 'strategicPartners'
  | 'contact'
  | 'about';

export type SiteFooterNavLink = {
  key: string;
  label: string;
  href: string;
  icon: SiteFooterNavIcon;
  external?: boolean;
};

export type SiteFooterSocialLink = {
  id: string;
  label: string;
  href?: string;
  icon?: string;
};

export type SiteFooterLegalLink = {
  label: string;
  href: string;
  external?: boolean;
};

export type GcsaFooterSnapshot = {
  title: string;
  email: string;
  emailLabel: string;
  /** 法律顾问 nav aria，与 common.footer.counselNav 一致 */
  counselNavAriaLabel?: string;
  copyright: string;
  navAriaLabel: string;
  legalAriaLabel: string;
  socialAriaLabel: string;
  socialPendingLabel: string;
  navLinks: SiteFooterNavLink[];
  socialLinks: SiteFooterSocialLink[];
  legalLinks: SiteFooterLegalLink[];
};

export const SHARED_HREFS = {
  home: `${GCSA_MAIN_ORIGIN}/`,
  products: `${GCSA_MAIN_ORIGIN}/products`,
  media: GCSA_MEDIA_ORIGIN,
  forums: `${GCSA_MAIN_ORIGIN}/forums`,
  aiResearchInstitute: `${GCSA_MAIN_ORIGIN}/ai-research-institute`,
  strategicPartners: `${GCSA_MAIN_ORIGIN}/strategic-partners`,
  contact: `${GCSA_MAIN_ORIGIN}/contact`,
  about: `${GCSA_MAIN_ORIGIN}/about`,
  privacy: `${GCSA_MAIN_ORIGIN}/privacy-policy`,
  terms: `${GCSA_MAIN_ORIGIN}/terms-of-use`,
  cookies: `${GCSA_MAIN_ORIGIN}/cookie-settings`,
  mailto: `mailto:${GCSA_CONTACT_EMAIL}`
} as const;

const FOOTER_NAV_ORDER: SiteFooterNavIcon[] = [
  'home',
  'products',
  'aiResearchInstitute',
  'strategicPartners',
  'forums',
  'media',
  'about',
  'contact'
];

export function buildNavLinks(
  labels: Record<SiteFooterNavIcon, string>,
  hrefs: Record<SiteFooterNavIcon, string> = {
    home: SHARED_HREFS.home,
    products: SHARED_HREFS.products,
    aiResearchInstitute: SHARED_HREFS.aiResearchInstitute,
    strategicPartners: SHARED_HREFS.strategicPartners,
    forums: SHARED_HREFS.forums,
    media: SHARED_HREFS.media,
    about: SHARED_HREFS.about,
    contact: SHARED_HREFS.contact
  }
): SiteFooterNavLink[] {
  return FOOTER_NAV_ORDER.map(key => ({
    key,
    label: labels[key],
    href: hrefs[key],
    icon: key,
    external: true
  }));
}

export const GCSA_FOOTER_BY_LOCALE: Record<SiteFooterLocale, GcsaFooterSnapshot> =
  GCSA_FOOTER_FROM_SITE_BY_LOCALE;

/** 默认：简体中文完整复刻 */
export const GCSA_FOOTER_DEFAULT = GCSA_FOOTER_BY_LOCALE['zh-CN'];

export function resolveGcsaFooter(locale: SiteFooterLocale = 'zh-CN'): GcsaFooterSnapshot {
  return GCSA_FOOTER_BY_LOCALE[locale] ?? GCSA_FOOTER_BY_LOCALE['en-US'];
}
